import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";

import Button from "components/Button";
import AuthLayout from "layouts/AuthLayout";
import { Frame } from "pages/QuoteFlow/CaseQuoteFrame";
import { startAccountGrant } from "pages/QuoteFlow/account-client";
import { REENTRY_NOT_FOUND_STATE } from "pages/QuoteFlow/reentry";
import QuoteFlowLoadingPage from "pages/QuoteFlow/QuoteFlowLoadingPage";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import { verifyPathFor } from "pages/StartLead/destinations";
import {
  leadFlowEvents,
  differentSurfaceOf,
  type ConfirmSurface,
  type EventPurpose,
  type SessionEndedStage,
} from "pages/StartLead/events";
import { useStatusClient } from "pages/StartLead/useStatusClient";
import type { ConfirmedVia } from "pages/StartLead/submissionStatus";
import type { ResumeNext } from "pages/StartResume/resume";
import LookUp from "pages/StartVerify/LookUp";
import { VERIFY_NOT_FOUND_STATE } from "pages/StartVerify/verify";

import { RetryButton, SupportCallLine } from "./GrantParts";
import {
  CHANGE_DESTINATION_LABEL,
  renewalButtonLabel,
  renewalMethodOf,
  SESSION_ENDED_BODY,
  SESSION_ENDED_ELSEWHERE_BODY,
  SESSION_ENDED_TITLE,
  SUPPORT_LINES,
  type RenewalTarget,
  type SupportLineKind,
} from "./login-grant-flow";
import {
  renewalOutcomeOf,
  renewalWaitCopy,
  renewGrant,
  type RenewalResult,
  type Renewed,
} from "./session-renewal";

type State =
  | { kind: "idle" }
  | { kind: "renewing" }
  | { kind: "wait"; rateLimited: boolean; at: number }
  | { kind: "look_up" };

/** The typed look-up's way back and its return page for what the grant was
 * made for. */
function lookUpFor(target: RenewalTarget | null): {
  redirect: string | undefined;
  notFound: { path: string; state: object };
} {
  if (target === null) {
    return { redirect: undefined, notFound: { path: "/start", state: {} } };
  }
  if (target.kind === "account") {
    const path = `/quotes/${encodeURIComponent(target.quoteId)}`;
    return {
      redirect: path,
      notFound: { path, state: REENTRY_NOT_FOUND_STATE },
    };
  }
  const path = verifyPathFor(target.submissionId.toLowerCase());
  return {
    redirect: path,
    notFound: { path, state: VERIFY_NOT_FOUND_STATE },
  };
}

/**
 * What a visitor sees when the grant behind a full-page code screen is gone:
 * a button that asks for a replacement, and the number to call beneath it.
 * When a replacement cannot be had, a screen hosted by a page that can
 * re-check (`onUnrenewable`) hands back to it; otherwise the visitor types
 * the email or phone to be sent a code. A wait the server asked for shows a
 * retry that opens after the cooldown.
 */
export default function SessionEnded({
  surface,
  supportKind,
  purpose,
  stage,
  target,
  onRenewed,
  onConfirmed,
  onUnrenewable,
}: {
  surface: ConfirmSurface;
  supportKind: SupportLineKind;
  purpose: EventPurpose;
  stage: SessionEndedStage;
  target: RenewalTarget | null;
  onRenewed: (renewed: Renewed) => void;
  onConfirmed: (
    next: ResumeNext,
    submissionId: string,
    via: ConfirmedVia,
  ) => void;
  /** Takes over when no replacement can be had. */
  onUnrenewable?: () => void;
}) {
  const trackEvent = useTrackLeadFlowEvent();
  const navigate = useNavigate();
  const client = useStatusClient();
  const handsBack = target === null && onUnrenewable !== undefined;
  const [state, setState] = useState<State>({ kind: "idle" });
  const [renewable, setRenewable] = useState(target !== null);
  const line = SUPPORT_LINES[supportKind];

  const reported = useRef(false);
  useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    trackEvent(
      leadFlowEvents.sessionEndedViewed({
        purpose,
        surface,
        stage,
        resolution: target === null ? "look_up" : "renew",
      }),
    );
  }, [trackEvent, purpose, surface, stage, target]);

  const handedBack = useRef(false);
  useEffect(() => {
    if (!handsBack || handedBack.current) return;
    handedBack.current = true;
    onUnrenewable?.();
  }, [handsBack, onUnrenewable]);

  const settle = (result: RenewalResult) => {
    trackEvent(
      leadFlowEvents.sessionRenewal({
        purpose,
        outcome: renewalOutcomeOf(result),
      }),
    );
    switch (result.kind) {
      case "renewed":
        onRenewed(result.renewed);
        return;
      case "confirmed":
        onConfirmed(result.next, result.submissionId, result.via);
        return;
      case "wait":
        setState({
          kind: "wait",
          rateLimited: result.rateLimited,
          at: Date.now(),
        });
        return;
      case "unavailable":
        if (onUnrenewable) {
          onUnrenewable();
          return;
        }
        setRenewable(false);
        setState({ kind: "idle" });
        return;
    }
  };

  const renew = async () => {
    if (target === null) return;
    setState({ kind: "renewing" });
    settle(
      await renewGrant(target, {
        mint: client.mint,
        accountGrant: (quoteId) =>
          startAccountGrant(quoteId, (error) => Sentry.captureException(error)),
      }),
    );
  };

  if (handsBack) return <QuoteFlowLoadingPage headerPhone={line} />;

  if (state.kind === "look_up") {
    const { redirect, notFound } = lookUpFor(target);
    return (
      <Frame shell="standalone" line={line}>
        <LookUp
          redirect={redirect}
          line={line}
          onNotFound={() =>
            navigate(notFound.path, { replace: true, state: notFound.state })
          }
          onSignedIn={() => navigate(redirect ?? "/start", { replace: true })}
          surface={surface}
        />
      </Frame>
    );
  }

  if (state.kind === "wait" && target !== null) {
    const copy = renewalWaitCopy(state.rateLimited);
    return (
      <AuthLayout title={copy.title} description={copy.body} headerPhone={line}>
        <div className="space-y-6">
          <RetryButton
            key={state.at}
            refusedAt={state.rateLimited ? state.at : 0}
            onRetry={() => {
              trackEvent(
                leadFlowEvents.confirmRetryClicked({
                  surface,
                  method: renewalMethodOf(target),
                }),
              );
              void renew();
            }}
          />
          <SupportCallLine
            kind={supportKind}
            surface={surface}
            screen="session_ended"
          />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={SESSION_ENDED_TITLE}
      description={
        target !== null && renewable
          ? SESSION_ENDED_BODY
          : SESSION_ENDED_ELSEWHERE_BODY
      }
      headerPhone={line}
    >
      <div className="space-y-6">
        {target !== null && renewable ? (
          <Button
            variant="continue"
            fullWidth
            onClick={() => void renew()}
            isLoading={state.kind === "renewing"}
            loadingText="Sending..."
          >
            {renewalButtonLabel(target)}
          </Button>
        ) : (
          <Button
            variant="continue"
            fullWidth
            onClick={() => {
              trackEvent(
                leadFlowEvents.useDifferentClicked(differentSurfaceOf(surface)),
              );
              setState({ kind: "look_up" });
            }}
          >
            {CHANGE_DESTINATION_LABEL}
          </Button>
        )}
        <SupportCallLine
          kind={supportKind}
          surface={surface}
          screen="session_ended"
        />
      </div>
    </AuthLayout>
  );
}
